import axios from "axios";
import { API_URL } from "helper/Constants";
import { ENCRYPTION_OF_DATA } from "helper/Functions";
import Cookies from "js-cookie"; // Importing js-cookie to manage cookiesl
class Auth {
  constructor(baseURL) {
    //console.log("AUTH BASE URL : ", baseURL);
    this.api = axios.create({
      baseURL: baseURL || API_URL, // Ensure this is your correct backend URL
    });

    this.lastModified = null; // Store Last-Modified timestamp

    // Add an interceptor to handle token refresh
    this.api.interceptors.request.use(
      async (config) => {
        const token = Cookies.get("token");
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        if (config.data) {
          config.headers["Content-Type"] = `application/json`;
          config.data = await ENCRYPTION_OF_DATA(config.data); // Send the entire encrypted object
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add a response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response && error.response.status === 401 && error.response.data.message !== "Invalid refresh token") {
          try {
            const newToken = await this.refreshToken();
            if (newToken) {
              // Update the token in the request header
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            window?.location?.reload();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Method to refresh the token using the refresh token

  // Method to register a new user
  async registerUser(profile) {
    try {
      const response = await this.api.post("/auth/register", {
        username: profile.username ?? "",
        password: profile.password ?? "",
        role: profile.role ?? "user",
        id: profile.id,
      });

      return {
        success: true,
        status: response.status,
        message: response.data.message,
      };
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de l'utilisateur :", error);

      if (error.response) {
        if (error.response.status === 409) {
          return {
            success: false,
            status: 409,
            message: "Utilisateur déjà enregistré",
          };
        }
        return {
          success: false,
          status: error.response.status,
          message: error.response.data.message || "Une erreur est survenue lors de l'enregistrement",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }
  async registerOnlineUser(profile) {
    try {
      const response = await this.api.post("/auth/online-register", {
        username: profile.username ?? "",
        password: profile.password ?? "",
        role: profile.role ?? "user",
        id: profile.id,
      });

      return {
        success: true,
        status: response.status,
        message: response.data.message,
      };
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de l'utilisateur :", error);

      if (error.response) {
        if (error.response.status === 409) {
          return {
            success: false,
            status: 409,
            message: "Utilisateur déjà enregistré",
          };
        }
        return {
          success: false,
          status: error.response.status,
          message: error.response.data.message || "Une erreur est survenue lors de l'enregistrement",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }

  async logoutAPICall() {
    try {
      const response = await this.api.post("/auth/logout", {}, { withCredentials: true });
      window.location.href = "/";
      return {
        success: true,
        status: response.status,
        message: response.data.message,
      };
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de l'utilisateur :", error);

      if (error.response) {
        if (error.response.status === 409) {
          return {
            success: false,
            status: 409,
            message: "Utilisateur déjà enregistré",
          };
        }
        return {
          success: false,
          status: error.response.status,
          message: error.response.data.message || "Une erreur est survenue lors de l'enregistrement",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }
  async refreshToken() {
    try {
      const response = await this.api.post(
        "/auth/refresh-token",
        {},
        {
          withCredentials: true, // Optional: Use this if you still want to send cookies
        }
      );
      const newAccessToken = response.data.accessToken;
      Cookies.set("token", newAccessToken, {
        secure: true, // Only transmit over HTTPS in production
        sameSite: "None", // Required for cross-origin
      }); // Save the new token
      return newAccessToken;
    } catch (error) {
      Cookies.remove("user", { secure: true, sameSite: "None" });
      Cookies.remove("token", { secure: true, sameSite: "None" });
      Cookies.remove("refreshToken", { secure: true, sameSite: "None" });
      window?.location?.reload();
      console.error("Failed to refresh token:", error);
      return null;
    }
  }

  // Method to login a user
  async loginUser(credentials) {
    try {
      const response = await this.api.post(
        "/auth/login",
        {
          username: credentials.username ?? "",
          password: credentials.password ?? "",
        },
        {
          withCredentials: true,
        }
      );
      // console.log(response?.data, "response auth file");

      // Store the tokens (both access and refresh tokens) in cookies
      if (response.data.user?.token || response.data.token) {
        Cookies.set("token", response.data.user.token ? response.data.user.token : response.data.token, {
          secure: true,
          sameSite: "None",
        });
      }

      return {
        success: true,
        status: response.status,
        token: response.data.user.token ? response.data.user.token : response.data.token,
        user: response.data.user,
        message: response.data.message,
      };
    } catch (error) {
      console.error("Erreur lors de la connexion de l'utilisateur :", error);

      if (error.response) {
        if (error.response.status === 401) {
          // Stop automatic refresh token requests
          Cookies.remove("refreshToken"); // Clear any existing token
          Cookies.remove("token"); // Clear any existing token
          return {
            success: false,
            status: 401,
            message: "Mot de passe incorrect",
          };
        }
        return {
          success: false,
          status: error.response.status,
          message: error.response.data?.message || "Une erreur est survenue lors de la connexion",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }

  async userChangePassword(credentials) {
    try {
      const response = await this.api.post(
        "/auth/change-password",
        {
          ...credentials,
        },
        {
          withCredentials: true,
        }
      );
      //console.log(response, "response auth file");

      // Store the tokens (both access and refresh tokens) in cookies
      if (response.data.success) {
        Cookies.remove("user", { secure: true, sameSite: "None" });
        Cookies.remove("token", { secure: true, sameSite: "None" });
        Cookies.remove("refreshToken", { secure: true, sameSite: "None" });
      }

      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error("Erreur lors de la connexion de l'utilisateur :", error);
      return {
        success: false,
        status: 500,
        message: error.response.data.message || "Network error or server is unreachable.",
      };
    }
  }

  async userUpdatePassword(credentials) {
    try {
      const response = await this.api.post(
        "/auth/update-user-password",
        {
          ...credentials,
        },
        {
          withCredentials: true,
        }
      );
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error("Erreur lors de la connexion de l'utilisateur :", error);
      return {
        success: false,
        status: 500,
        message: error.response.data.message || "Network error or server is unreachable.",
      };
    }
  }

  // Method to get users by role
  async getUsersByRole(role) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.post("/auth/usersByRole", { role: role ?? "" }, { headers: { Authorization: `Bearer ${token}` } });

      return {
        success: true,
        status: response.status,
        users: response.data.users,
      };
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs par rôle :", error);

      if (error.response) {
        return {
          success: false,
          status: error.response.status,
          message: error.response.data.message || "Une erreur est survenue lors de la récupération des utilisateurs",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }

  async getAllDesendentUsers(creatorId) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.get(`/auth/getAllUsersByCreatorId?createrid=${creatorId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return {
        success: true,
        status: response.status,
        users: response.data.users,
      };
    } catch (error) {
      console.error("Erreur lors de la récupération de tous les utilisateurs :", error);

      if (error.response) {
        return {
          success: false,
          status: error.response.status,
          message: error.response.data.message || "Une erreur est survenue lors de la récupération des utilisateurs",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }
  async getAllCreaterUsersById(creatorId) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.get(`/auth/getAllCreatorUsers?createrid=${creatorId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return {
        success: true,
        status: response.status,
        users: response.data.users,
      };
    } catch (error) {
      console.error("Erreur lors de la récupération de tous les utilisateurs :", error);

      if (error.response) {
        return {
          success: false,
          status: error.response.status,
          message: error.response.data.message || "Une erreur est survenue lors de la récupération des utilisateurs",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }
  // Method to delete user by username
  async deleteUserByUsername(username) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.delete("/auth/delete_user", {
        data: { username: username ?? "" },
        headers: { Authorization: `Bearer ${token}` },
      });

      return {
        success: true,
        status: response.status,
        message: response.data.message,
      };
    } catch (error) {
      console.error("Erreur lors de la suppression de l'utilisateur :", error);

      if (error.response) {
        return {
          success: false,
          status: error.response.status,
          message: error.response.data.message || "Une erreur est survenue lors de la suppression de l'utilisateur",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }

  // Method to get all users
  async getAllUsers(filters = {}, signal = null) {
    const token = Cookies.get("token");
    const {
      searchText = "",
      role = "",
      registrationType = "",
      limit = 10, // default 10 users per batch
      offset = 0, // starting offset
      start, // optional start
      end, // optional end
    } = filters;
    const actualOffset = typeof start === "number" ? start : offset;
    const actualLimit = typeof end === "number" ? end - (start || 0) : limit;

    // API GET request with params for pagination & filtering
    const response = await this.api.get("/auth/getallusers", {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        searchText,
        role,
        registrationType,
        limit: actualLimit,
        offset: actualOffset,
      },
      signal,
    });

    return {
      success: true,
      status: response.status,
      users: response.data.users,
    };
  }

  async getAllUsersWithSearchText(filters = {}) {
  const token = Cookies.get("token");
  const {
  searchText = "",
  role = "",
  registrationType = "",
  limit = 10,   // default 10 users per batch
  offset = 0,   // starting offset
  start,        // optional start
  end           // optional end
} = filters;
    const actualOffset = typeof start === 'number'? start:offset;
    const actualLimit = typeof end === "number"? end-(start||0):limit

  // API GET request with params for pagination & filtering
  const response = await this.api.get("/auth/getallusers", {
    headers: { Authorization: `Bearer ${token}` },
    params: {
      searchText,
      role,
      registrationType,
      limit:actualLimit,
      offset:actualOffset
    }
  });

  return {
    success: true,
    status: response.status,
    users: response.data.users
  };
}


  // Method to get the balance of a user
  async getBalance(username) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.post("/auth/getbalance", { username: username ?? "" }, { headers: { Authorization: `Bearer ${token}` } });

      return {
        success: true,
        status: response.status,
        balance: response.data.balance,
      };
    } catch (error) {
      console.error("Erreur lors de la récupération du solde de l'utilisateur :", error);

      if (error.response) {
        return {
          success: false,
          status: error.response.status,
          message: error.response.data.message || "Une erreur est survenue lors de la récupération du solde",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }

  async updateBalance(headers = {}) {
    try {
      const token = Cookies.get("token");

      // Add If-Modified-Since header if we have a cached Last-Modified value
      if (this.lastModified) {
        headers["If-Modified-Since"] = this.lastModified;
      }

      const response = await this.api.get("/auth/ubalance", {
        headers: {
          Authorization: `Bearer ${token}`,
          ...headers,
        },
      });

      // Save Last-Modified header if available
      if (response.headers["last-modified"]) {
        this.lastModified = response.headers["last-modified"];
      }

      return {
        success: true,
        status: response.status,
        balance: response.data.balance,
        lastModified: this.lastModified,
      };
    } catch (error) {
      console.error("Error fetching user balance:", error);

      if (error.response) {
        // Handle 304 Not Modified status
        if (error.response.status === 304) {
          return {
            success: true,
            status: 304,
            message: "Balance not modified",
          };
        }

        return {
          success: false,
          status: error.response.status,
          message: error.response.data.message || "Error retrieving balance",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }
  

  // Method to get users by creator ID
  async getUsersByCreatorId(creatorId) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.get(`/auth/usersByCreater/${creatorId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return {
        success: true,
        status: response.status,
        users: response.data.user,
      };
    } catch (error) {
      console.error("Error fetching users by creator ID:", error);

      if (error.response) {
        return {
          success: false,
          status: error.response.status,
          message: error.response.data.message || "Error retrieving users.",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }
}

export default Auth;
